import React, { useState } from 'react';
import { activityCsv, money } from '../shared/pulseMetrics';

const PHONE_ROWS = 5;

function countLabel(count, one, many) {
  const value = Number(count) || 0;
  return `${value} ${value === 1 ? one : many}`;
}

function percent(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `${Math.round(Number(value))}%`;
}

function when(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Sparkline({ points, color }) {
  const values = (points || []).map((value) => Number(value) || 0);
  if (values.length < 2) return null;
  const width = 76;
  const height = 28;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const d = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - (((value - min) / span) * (height - 4)) - 2;
    return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LineChart({ points, color, narrow }) {
  const width = 520;
  const height = 160;
  const pad = 16;
  const values = points.map((point) => point.cents || 0);
  const max = Math.max(...values, 1);
  const coords = values.map((value, index) => {
    const x = pad + (index / Math.max(values.length - 1, 1)) * (width - pad * 2);
    const y = height - pad - (value / max) * (height - pad * 2);
    return { x, y };
  });
  const d = coords.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={narrow ? 140 : 180} role="img" aria-label="Monthly recurring revenue, last 12 months">
      <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((point, index) => (
        <circle key={points[index].month} cx={point.x} cy={point.y} r="2.5" fill={color} />
      ))}
      <text x={pad} y={14} fill="#8a8a8a" fontSize="11">{points[0] && points[0].label}</text>
      <text x={width - pad} y={14} fill="#8a8a8a" fontSize="11" textAnchor="end">{last && last.label}</text>
    </svg>
  );
}

function SignupChart({ points, color, narrow }) {
  const width = 520;
  const height = 160;
  const max = Math.max(1, ...points.map((point) => point.agents + point.homeowners));
  const slot = width / points.length;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={narrow ? 140 : 180} role="img" aria-label="Weekly signups, agents and homeowners">
      {points.map((point, index) => {
        const agents = (point.agents / max) * (height - 28);
        const homeowners = (point.homeowners / max) * (height - 28);
        const x = index * slot + slot * 0.2;
        const bar = slot * 0.28;
        return (
          <g key={point.week}>
            <rect x={x} y={height - 18 - agents} width={bar} height={agents} rx="2" fill={color} />
            <rect x={x + bar + 2} y={height - 18 - homeowners} width={bar} height={homeowners} rx="2" fill="#c7c7cc" />
          </g>
        );
      })}
    </svg>
  );
}

function PulseList({ id, title, rows, narrow, empty, renderRow, frame }) {
  const [open, setOpen] = useState(false);
  const limit = narrow && !open ? PHONE_ROWS : rows.length;
  const shown = rows.slice(0, limit);
  return (
    <section style={{ ...styles.panel, ...frame }} aria-label={title}>
      <h3 style={styles.kicker}>{title}</h3>
      {shown.length === 0 ? <p style={styles.empty}>{empty}</p> : (
        <div>
          {shown.map((row) => (
            <div key={row.id} data-testid={`${id}-row`}>{renderRow(row)}</div>
          ))}
        </div>
      )}
      {narrow && rows.length > PHONE_ROWS ? (
        <button type="button" style={styles.textButton} onClick={() => setOpen((value) => !value)}>
          {open ? 'Show less' : 'See all'}
        </button>
      ) : null}
    </section>
  );
}

function RowButton({ row, onOpen, children }) {
  const clickable = row.targetType && row.targetId && onOpen;
  if (!clickable) return <div style={styles.row}>{children}</div>;
  return (
    <button type="button" style={styles.rowButton} onClick={() => onOpen(row)}>
      {children}
    </button>
  );
}

function PulseDashboard({
  colors,
  narrow,
  pulse,
  error,
  onOpen,
  onExport,
}) {
  const ink = colors || {};
  const text = ink.text || '#1a1a1a';
  const muted = ink.textMuted || '#8a8a8a';
  const secondary = ink.textSecondary || '#666';
  const line = ink.border || '#ececec';
  const surface = ink.surface || '#fff';
  const accent = ink.primary || '#59c4c4';
  if (error) {
    return <p style={{ color: text, margin: 0 }}>{error}</p>;
  }
  if (!pulse) {
    return <p style={{ color: muted, margin: 0 }}>Loading Pulse…</p>;
  }
  const mrrLive = Boolean(pulse.charts && pulse.charts.mrrLive);
  const mrrPoints = mrrLive ? ((pulse.charts && pulse.charts.mrr) || []) : [];
  const signupPoints = (pulse.charts && pulse.charts.signups) || [];
  const sandbox = pulse.stripe && pulse.stripe.mode === 'live' ? null : pulse.sandbox;
  const liveBilling = Boolean(pulse.stripe && pulse.stripe.mode === 'live');
  const showCustomers = Boolean(pulse.mrr.live || (liveBilling && pulse.mrr.customers > 0));
  const cards = [
    {
      id: 'paying',
      label: 'Paying agents',
      value: String(pulse.mrr.payingAgents || 0),
      detail: `${pulse.mrr.teamCustomers || 0} teams · ${pulse.mrr.seats || 0} seats`,
      spark: mrrLive ? mrrPoints.map((point) => point.cents) : null,
    },
    {
      id: 'trials',
      label: 'Active trials',
      value: String(pulse.trials.active || 0),
      detail: `${percent(pulse.trials.trialToPaidPct)} started paying in 30 days`,
    },
    {
      id: 'churn',
      label: 'Canceled, 30 days',
      value: String(pulse.churn.canceled30d || 0),
      detail: `${pulse.churn.cancellingAtPeriodEnd || 0} set to cancel · ${percent(pulse.churn.ratePct)}`,
    },
    {
      id: 'signups',
      label: 'Signups, 7 days',
      value: String((pulse.signups.d7.agents || 0) + (pulse.signups.d7.homeowners || 0)),
      detail: `${pulse.signups.d7.agents} agents · ${pulse.signups.d7.homeowners} homeowners`,
      spark: signupPoints.map((point) => point.agents + point.homeowners),
    },
    {
      id: 'homeowners',
      label: 'Homeowners active',
      value: String(pulse.homeowners.active30d || 0),
      detail: `${pulse.homeowners.invitedNotJoined} invited · ${pulse.homeowners.archived} archived`,
    },
    {
      id: 'teams',
      label: 'Teams',
      value: String(pulse.teams.count || 0),
      detail: `${pulse.teams.seatsUsed} of ${pulse.teams.seatsTotal} seats`,
    },
    {
      id: 'agents',
      label: 'Agents',
      value: String(pulse.agents.total || 0),
      detail: `${pulse.agents.owners} owners · ${pulse.agents.members} members`,
    },
    {
      id: 'ai',
      label: 'AI questions',
      value: String(pulse.ai.questions || 0),
      detail: `of ${pulse.ai.cap} · ${pulse.ai.perHomeowner} per homeowner`,
    },
  ];

  const frame = { background: surface, border: `1px solid ${line}` };
  const download = () => {
    const csv = activityCsv(pulse.activity || []);
    if (onExport) onExport(csv);
  };

  const rowBody = {
    borderBottom: `1px solid ${line}`,
    color: text,
  };

  return (
    <div data-testid="pulse-page" style={{ color: text, fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={styles.titleRow}>
        <div>
          <h2 style={{ ...styles.title, color: text }}>Pulse</h2>
          <p style={{ ...styles.note, color: muted }}>{pulse.stripe.note}</p>
        </div>
        {pulse.stripe.badge ? (
          <span style={{ ...styles.badge, background: ink.primaryLight || 'rgba(89, 196, 196, 0.12)', color: accent }}>
            {pulse.stripe.badge}
          </span>
        ) : null}
      </div>

      <section style={{ ...styles.hero, background: surface, border: `1px solid ${line}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-end' }}>
          <div>
            <div style={{ ...styles.kicker, color: muted }}>Monthly recurring</div>
            <div style={styles.heroValue}>{money(pulse.mrr.cents)}</div>
            <div style={{ color: secondary, marginTop: '0.25rem' }}>
              {pulse.mrr.live ? `Annual run rate ${money(pulse.mrr.arrCents)}` : (pulse.mrr.emptyLabel || 'No live revenue yet')}
            </div>
            {showCustomers ? (
              <div style={{ color: muted, marginTop: '0.35rem', fontSize: '0.85rem' }}>
                {countLabel(pulse.mrr.customers, 'customer', 'customers')}
                {pulse.mrr.live && pulse.mrr.seats ? ` · ${countLabel(pulse.mrr.seats, 'team seat', 'team seats')}` : ''}
              </div>
            ) : null}
          </div>
          {mrrLive ? <Sparkline points={mrrPoints.map((point) => point.cents)} color={accent} /> : null}
        </div>
        {pulse.mrr.live ? (
        <div style={{ ...styles.split, gridTemplateColumns: narrow ? '1fr 1fr' : 'repeat(5, minmax(0, 1fr))' }}>
          {pulse.mrr.lines.filter((lineItem) => lineItem.cents > 0).map((lineItem) => (
            <div key={lineItem.id}>
              <div style={{ ...styles.kicker, color: muted }}>{lineItem.label}</div>
              <div style={{ fontWeight: 600, marginTop: '0.2rem' }}>{money(lineItem.cents)}</div>
              <div style={{ color: muted, fontSize: '0.78rem', marginTop: '0.15rem' }}>
                {lineItem.customers ? countLabel(lineItem.customers, 'customer', 'customers') : ''}
                {lineItem.id === 'team' && lineItem.seats ? `${lineItem.customers ? ' · ' : ''}${countLabel(lineItem.seats, 'seat', 'seats')}` : ''}
                {(lineItem.id === 'addon_plus' || lineItem.id === 'addon_max') && lineItem.seats ? `${lineItem.customers ? ' · ' : ''}${countLabel(lineItem.seats, 'seat', 'seats')}` : ''}
              </div>
            </div>
          ))}
        </div>
        ) : null}
      </section>

      {sandbox ? (
        <section
          aria-label="Test payments (sandbox, not real revenue)"
          style={{ ...styles.hero, background: ink.surfaceAlt || '#f4f4f5', border: `1px solid ${line}`, color: muted }}
        >
          <div style={{ ...styles.kicker, color: muted }}>{sandbox.label}</div>
          <div style={{ ...styles.heroValue, fontSize: '1.7rem', color: secondary }}>{money(sandbox.cents)}</div>
          <div style={{ color: muted, marginTop: '0.25rem' }}>Annual run rate {money(sandbox.arrCents)}</div>
          <div style={{ ...styles.split, gridTemplateColumns: narrow ? '1fr 1fr' : 'repeat(5, minmax(0, 1fr))' }}>
            {(sandbox.lines || []).filter((lineItem) => lineItem.cents > 0).map((lineItem) => (
              <div key={lineItem.id}>
                <div style={{ ...styles.kicker, color: muted }}>{lineItem.label}</div>
                <div style={{ fontWeight: 600, marginTop: '0.2rem', color: secondary }}>{money(lineItem.cents)}</div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div style={{ ...styles.grid, gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))' }}>
        {cards.map((card) => (
          <article key={card.id} style={{ ...styles.card, background: surface, border: `1px solid ${line}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
              <div style={{ ...styles.kicker, color: muted }}>{card.label}</div>
              {card.spark ? <Sparkline points={card.spark} color={accent} /> : null}
            </div>
            <div style={styles.cardValue}>{card.value}</div>
            <div style={{ color: secondary, fontSize: '0.82rem', lineHeight: 1.35 }}>{card.detail}</div>
          </article>
        ))}
      </div>
      <p style={{ ...styles.note, color: muted }}>
        Today {pulse.signups.today.agents} agents and {pulse.signups.today.homeowners} homeowners.
        {' '}30 days {pulse.signups.d30.agents} agents and {pulse.signups.d30.homeowners} homeowners.
      </p>

      <div style={{ ...styles.charts, gridTemplateColumns: narrow ? '1fr' : '1.15fr 0.85fr' }}>
        <section style={{ ...styles.panel, background: surface, border: `1px solid ${line}` }}>
          <h3 style={{ ...styles.kicker, color: muted }}>Revenue, 12 months</h3>
          {mrrLive && mrrPoints.length ? (
            <>
              <LineChart points={mrrPoints} color={accent} narrow={narrow} />
              <p style={{ ...styles.note, color: muted, margin: 0 }}>Latest month {money(mrrPoints[mrrPoints.length - 1].cents)}</p>
            </>
          ) : (
            <p style={{ ...styles.empty, marginTop: '1.25rem' }}>{liveBilling ? 'No live revenue yet' : 'Live billing not on yet'}</p>
          )}
        </section>
        <section style={{ ...styles.panel, background: surface, border: `1px solid ${line}` }}>
          <h3 style={{ ...styles.kicker, color: muted }}>Weekly signups</h3>
          <SignupChart points={signupPoints} color={accent} narrow={narrow} />
          <p style={{ ...styles.note, color: muted, margin: 0 }}>Teal is agents. Gray is homeowners.</p>
        </section>
      </div>

      <div style={{ ...styles.charts, gridTemplateColumns: narrow ? '1fr' : '1fr 1fr' }}>
        <PulseList
          frame={frame}
          id="signup"
          title="Recent signups"
          rows={pulse.lists.recentSignups}
          narrow={narrow}
          empty="No signups yet."
          renderRow={(row) => (
            <RowButton row={row} onOpen={onOpen}>
              <div style={rowBody}>
                <strong>{row.name}</strong>
                <div style={{ color: muted, fontSize: '0.82rem' }}>{row.kind === 'agent' ? 'Agent' : 'Homeowner'} · {when(row.at)}</div>
              </div>
            </RowButton>
          )}
        />
        <PulseList
          frame={frame}
          id="trial"
          title="Trials ending in 7 days"
          rows={pulse.lists.trialsEnding}
          narrow={narrow}
          empty="No trials end this week."
          renderRow={(row) => (
            <RowButton row={row} onOpen={onOpen}>
              <div style={rowBody}>
                <strong>{row.name}</strong>
                <div style={{ color: muted, fontSize: '0.82rem' }}>{row.plan} · ends {when(row.at)}</div>
              </div>
            </RowButton>
          )}
        />
        <PulseList
          frame={frame}
          id="payment"
          title="Failed payments"
          rows={pulse.lists.failedPayments}
          narrow={narrow}
          empty="No failed payments."
          renderRow={(row) => (
            <RowButton row={row} onOpen={onOpen}>
              <div style={rowBody}>
                <strong>{row.name}</strong>
                <div style={{ color: muted, fontSize: '0.82rem' }}>{money(row.amountCents)} · {when(row.at)}{row.test ? ' · Test' : ''}</div>
              </div>
            </RowButton>
          )}
        />
        <PulseList
          frame={frame}
          id="inquiry"
          title="Enterprise inquiries"
          rows={pulse.lists.inquiries}
          narrow={narrow}
          empty="No inquiries."
          renderRow={(row) => (
            <RowButton row={row} onOpen={onOpen}>
              <div style={rowBody}>
                <strong>{row.name}</strong>
                <div style={{ color: muted, fontSize: '0.82rem' }}>{row.company || 'No company'} · {row.agentCount} agents</div>
              </div>
            </RowButton>
          )}
        />
      </div>

      <PulseList
        frame={frame}
        id="error"
        title="Errors"
        rows={pulse.lists.errors}
        narrow={narrow}
        empty="No recent app errors or failed jobs."
        renderRow={(row) => (
          <div style={{ ...styles.row, borderBottom: `1px solid ${line}` }}>
            <strong>{row.title}</strong>
            <div style={{ color: muted, fontSize: '0.82rem' }}>{row.source === 'job' ? 'Job' : 'App'} · {when(row.at)}{row.detail ? ` · ${row.detail}` : ''}</div>
          </div>
        )}
      />

      <section style={{ ...styles.panel, background: surface, border: `1px solid ${line}` }} aria-label="Activity">
        <div style={styles.titleRow}>
          <h3 style={{ ...styles.kicker, color: muted, margin: 0 }}>Activity</h3>
          <button type="button" style={{ ...styles.textButton, color: accent }} onClick={download}>Export CSV</button>
        </div>
        <ActivityRows rows={pulse.activity || []} narrow={narrow} onOpen={onOpen} line={line} muted={muted} />
      </section>
    </div>
  );
}

function ActivityRows({ rows, narrow, onOpen, line, muted }) {
  const [open, setOpen] = useState(false);
  const limit = narrow && !open ? PHONE_ROWS : Math.min(rows.length, 20);
  const shown = rows.slice(0, limit);
  return (
    <div>
      {shown.length === 0 ? <p style={styles.empty}>No activity yet.</p> : shown.map((row) => (
        <div key={row.id} data-testid="activity-row">
          <RowButton row={row} onOpen={onOpen}>
            <div style={{ borderBottom: `1px solid ${line}` }}>
              <strong>{row.title}</strong>
              <div style={{ color: muted, fontSize: '0.82rem' }}>{when(row.at)}{row.detail ? ` · ${row.detail}` : ''}</div>
            </div>
          </RowButton>
        </div>
      ))}
      {narrow && rows.length > PHONE_ROWS ? (
        <button type="button" style={styles.textButton} onClick={() => setOpen((value) => !value)}>
          {open ? 'Show less' : 'See all'}
        </button>
      ) : null}
    </div>
  );
}

const styles = {
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 500,
    letterSpacing: '-0.03em',
    margin: 0,
  },
  note: {
    fontSize: '0.85rem',
    margin: '0.35rem 0 0',
  },
  badge: {
    padding: '0.3rem 0.65rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  },
  hero: {
    borderRadius: '1.1rem',
    padding: '1.25rem',
    marginBottom: '0.9rem',
  },
  heroValue: {
    fontSize: '2.4rem',
    fontWeight: 500,
    letterSpacing: '-0.04em',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1,
  },
  split: {
    display: 'grid',
    gap: '0.85rem',
    marginTop: '1.1rem',
  },
  grid: {
    display: 'grid',
    gap: '0.75rem',
  },
  card: {
    borderRadius: '1rem',
    padding: '0.95rem 1rem 1rem',
    minWidth: 0,
  },
  cardValue: {
    fontSize: '1.7rem',
    fontWeight: 500,
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
    margin: '0.15rem 0 0.25rem',
  },
  kicker: {
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    margin: 0,
  },
  charts: {
    display: 'grid',
    gap: '0.75rem',
    marginTop: '0.9rem',
  },
  panel: {
    borderRadius: '1rem',
    padding: '1rem 1rem 0.4rem',
    marginTop: '0.75rem',
  },
  row: {
    padding: '0.75rem 0',
  },
  rowButton: {
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: 'none',
    padding: '0.75rem 0',
    cursor: 'pointer',
    font: 'inherit',
  },
  textButton: {
    background: 'transparent',
    border: 'none',
    padding: '0.65rem 0 0.8rem',
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: 600,
    color: '#59c4c4',
  },
  empty: {
    color: '#8a8a8a',
    margin: '0.75rem 0',
    fontSize: '0.9rem',
  },
};

export default PulseDashboard;
export { PHONE_ROWS };
